import { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { PhoneHoursScheduleRule } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Heading, Text, Truncated } from '@frontend/design-system';
import { groupByDayOfWeek } from '../../utils/dates';
import { buildEvents, CalendarEvent, formatTime, getPosition } from './calendar-view.helpers';

interface CalendarViewProps {
  phoneHours: PhoneHoursScheduleRule[];
  onCanShowArrowsChange?: (canShowArrows: boolean) => void;
}

const PIXELS_PER_HOUR = 72;

const ORDERED_DAYS = [1, 2, 3, 4, 5, 6, 0] as const;

const CalendarView = ({ phoneHours, onCanShowArrowsChange }: CalendarViewProps) => {
  const { t } = useTranslation('phone');

  const dayHours = useMemo(() => groupByDayOfWeek(phoneHours), [phoneHours]);
  const dayNames = useMemo(() => [t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun')], [t]);

  // Calculate dynamic start and end hours based on actual schedule
  const { START_HOUR, END_HOUR } = useMemo(() => {
    const DEFAULT_START_HOUR = 8;
    const DEFAULT_END_HOUR = 18;

    let earliestHour = Infinity;
    let latestHour = -Infinity;

    Object.values(dayHours).forEach((dayData) => {
      if (!dayData) return;

      const collectHour = (time?: string | null, isEnd = false) => {
        if (!time) return;

        const parsed = dayjs(time, 'HH:mm:ss');
        let hour = parsed.hour();

        if (isEnd && parsed.minute() > 0) {
          hour += 1; // round up
        }

        earliestHour = Math.min(earliestHour, hour);
        latestHour = Math.max(latestHour, hour);
      };

      collectHour(dayData.openStartTime);
      collectHour(dayData.openEndTime, true);

      dayData.breaks?.forEach((b) => {
        collectHour(b.startTime);
        collectHour(b.endTime, true);
      });
    });

    // Fallback if no data
    if (earliestHour === Infinity || latestHour === -Infinity) {
      earliestHour = DEFAULT_START_HOUR;
      latestHour = DEFAULT_END_HOUR;
    }

    return {
      START_HOUR: Math.max(0, earliestHour - 1),
      END_HOUR: Math.min(23, latestHour + 1),
    };
  }, [dayHours]);

  const totalHeight = useMemo(() => (END_HOUR - START_HOUR) * PIXELS_PER_HOUR, [END_HOUR, START_HOUR]);

  const timeSlots = useMemo(
    () =>
      Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => {
        const hour = i + START_HOUR;
        return dayjs().hour(hour).minute(0).format('h A');
      }),
    [END_HOUR, START_HOUR]
  );

  // Precompute events for each day once per dayHours change (instead of 7x per render)
  const eventsByDay = useMemo(() => {
    const result: Record<number, CalendarEvent[]> = {};
    ORDERED_DAYS.forEach((dayIndex) => {
      result[dayIndex] = buildEvents(dayHours[dayIndex], t);
    });
    return result;
  }, [dayHours, t]);

  // -----------------------------
  // Refs
  // -----------------------------
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollPositionRef = useRef(0);
  const isSyncingRef = useRef(false);

  // -----------------------------
  // Constants
  // -----------------------------
  const columnWidth = 146;
  const actualTimeColumnWidth = 50;
  const totalColumns = 7;

  // -----------------------------
  // Layout width (stable container ONLY)
  // -----------------------------
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [stableCanShowArrows, setStableCanShowArrows] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // -----------------------------
  // Scroll position for arrow states (triggers re-renders)
  // -----------------------------
  const [scrollPosition, setScrollPosition] = useState(0);

  // -----------------------------
  // Tracks the calendar's available width in `layoutWidth`, which the
  // arrow visibility logic uses to decide whether all 7 day columns fit.
  //
  // We measure the outer wrapper (not the scrollable area) so scrollbar
  // appearance doesn't change the measurement and cause arrows to flicker
  // on/off. A ResizeObserver keeps it in sync; rAF + a 1px epsilon
  // prevent redundant state updates.
  // -----------------------------
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;

    let raf = 0;

    const update = () => {
      cancelAnimationFrame(raf);

      raf = requestAnimationFrame(() => {
        // wrapper width is stable (no scrollbar influence now)
        const width = el.clientWidth - 32;

        setLayoutWidth((prev) => (Math.abs(prev - width) < 1 ? prev : width));
      });
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  // -----------------------------
  // Scroll handler
  // -----------------------------
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;

    scrollPositionRef.current = scrollLeft;
    setScrollPosition(scrollLeft);

    if (isSyncingRef.current) return;

    if (e.currentTarget === scrollRef.current && headerScrollRef.current) {
      isSyncingRef.current = true;

      headerScrollRef.current.scrollLeft = scrollLeft;

      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }
  };

  // -----------------------------
  // Header scroll handler
  // (header's native scroll event drives scrollPosition during smooth scroll animations)
  // -----------------------------
  const handleHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    scrollPositionRef.current = scrollLeft;
    setScrollPosition(scrollLeft);
  };

  // -----------------------------
  // Scroll actions
  // -----------------------------
  const scrollByColumn = (direction: -1 | 1) => {
    const amount = columnWidth * direction;

    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
    headerScrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const scrollLeft = () => scrollByColumn(-1);
  const scrollRight = () => scrollByColumn(1);

  // -----------------------------
  // SAFE derived values
  // -----------------------------
  const width = Math.max(0, layoutWidth - actualTimeColumnWidth);

  const totalScrollWidth = totalColumns * columnWidth;
  const maxScrollLeft = Math.max(0, totalScrollWidth - width);

  // -----------------------------
  // Arrow visibility — uses two thresholds to avoid flicker.
  //
  // Arrows show when the 7 day columns don't fit. But rendering the arrows
  // itself shifts the layout (~8px), which can flip the fit-decision back
  // and cause the arrows to flicker on/off near the boundary width.
  //
  // To prevent this, we use a different threshold for showing vs. hiding:
  // once arrows are shown, the container has to grow noticeably wider
  // before we hide them again (and vice-versa). This "dead zone" between
  // the two thresholds keeps the decision stable near the boundary.
  // -----------------------------
  const isAtStart = scrollPosition <= 0;
  const isAtEnd = scrollPosition >= maxScrollLeft - 1;

  // Check if content actually fits, but with hysteresis to prevent feedback loop
  const allColumnsWidth = totalColumns * columnWidth; // 7 * 146 = 1022px
  const availableContentWidth = layoutWidth - actualTimeColumnWidth; // subtract time column

  // Use different thresholds for showing vs hiding arrows (hysteresis)
  // Parent gap change is ~8px, so use larger buffer
  const SHOW_ARROWS_THRESHOLD = allColumnsWidth + 20; // Show arrows when tight
  const HIDE_ARROWS_THRESHOLD = allColumnsWidth + 50; // Hide arrows when loose

  const currentCanShowArrows = stableCanShowArrows
    ? availableContentWidth < HIDE_ARROWS_THRESHOLD // Currently showing: need more space to hide
    : availableContentWidth < SHOW_ARROWS_THRESHOLD; // Currently hidden: need less space to show

  // Debounce commits to `stableCanShowArrows` by 100ms.
  //
  // During a resize (window drag, parent layout change, etc.), `layoutWidth`
  // can fire many ResizeObserver updates in rapid succession, and
  // `currentCanShowArrows` may flip true/false several times as the width
  // crosses the hysteresis thresholds. Without this debounce the arrows
  // would visibly toggle on/off during the drag. Waiting 100ms of "quiet"
  // before committing means we only render the final settled state.
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setStableCanShowArrows(currentCanShowArrows);
    }, 100); // Medium debounce for stable decisions

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [currentCanShowArrows]);

  const canShowArrows = stableCanShowArrows;

  // -----------------------------
  // Effects
  // -----------------------------
  useEffect(() => {
    onCanShowArrowsChange?.(canShowArrows);
  }, [canShowArrows, onCanShowArrowsChange]);

  // -----------------------------
  // Disabled states
  // -----------------------------
  const isLeftDisabled = isAtStart;
  const isRightDisabled = isAtEnd;

  return (
    <div
      ref={containerRef}
      css={{
        boxShadow: '0px 1px 1px 0px rgba(0, 33, 82, 0.15), 0px 1px 3px 1px rgba(0, 33, 82, 0.15)',
        borderRadius: 'var(--fabric-radius-md)',
        padding: 'var(--fabric-space-padding-lg)',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '90vw',
        margin: '0 auto',
      }}
    >
      <div
        css={{
          ...(!canShowArrows && {
            width: 'fit-content',
            margin: '0 auto',
          }),
        }}
      >
        {' '}
        <Heading level={3} css={{ marginBottom: 'var(--fabric-space-gap-md)' }}>
          {t('Calendar View')}
        </Heading>
        {/* Day Names Header */}
        <div
          css={{
            display: 'flex',
            marginBottom: 'var(--fabric-space-gap-xs)',
            position: 'relative',
          }}
        >
          {/* Fixed time column header */}
          <div css={{ width: 'fit-content' }}>
            <Text
              size='small'
              css={{
                marginRight: 'var(--fabric-space-gap-xs)',
                width: 'fit-content',
                opacity: 0,
                pointerEvents: 'none',
              }}
            >
              {t('12 PM')}
            </Text>
          </div>

          {/* Fixed left arrow - positioned relative to the whole header */}
          {canShowArrows && (
            <Icon
              name='caret-left-small'
              color={isLeftDisabled ? 'disabled' : 'subdued'}
              onClick={isLeftDisabled ? undefined : scrollLeft}
              css={{
                position: 'absolute',
                left: 40,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: isLeftDisabled ? 'default' : 'pointer',
                zIndex: 10,
                '&:hover': isLeftDisabled
                  ? {}
                  : {
                      backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 6,
                      transform: 'translateY(-50%) scale(1.08)',
                      transition: 'all 120ms ease',
                    },
              }}
            />
          )}

          {/* Fixed right arrow - positioned relative to the whole header */}
          {canShowArrows && (
            <Icon
              name='caret-right-small'
              color={isRightDisabled ? 'disabled' : 'subdued'}
              onClick={isRightDisabled ? undefined : scrollRight}
              css={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: isRightDisabled ? 'default' : 'pointer',
                zIndex: 10,
                '&:hover': isRightDisabled
                  ? {}
                  : {
                      backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 6,
                      transform: 'translateY(-50%) scale(1.08)',
                      transition: 'all 120ms ease',
                    },
              }}
            />
          )}

          {/* Scrollable day headers */}
          <div
            ref={headerScrollRef}
            onScroll={handleHeaderScroll}
            css={{
              flex: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              '::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 146px)',
                minWidth: 'fit-content',
              }}
            >
              {dayNames.map((day) => {
                return (
                  <div
                    key={day}
                    css={{
                      padding: 8,
                      textAlign: 'center',
                      position: 'relative',
                    }}
                  >
                    <Text size='medium' color='subdued' weight='semibold'>
                      {day}
                    </Text>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div
          css={{
            display: 'flex',
          }}
        >
          {/* Fixed Time Column */}
          <div
            css={{
              width: 'fit-content',
            }}
          >
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {timeSlots.map((time) => (
                <Text
                  key={time}
                  size='small'
                  color='subdued'
                  weight='semibold'
                  css={{
                    // height: PIXELS_PER_HOUR,
                    height: PIXELS_PER_HOUR,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    transform: 'translateY(-50%)',
                    marginRight: 'var(--fabric-space-gap-xs)',
                    width: 'fit-content',
                  }}
                >
                  {time}
                </Text>
              ))}
            </div>
          </div>

          {/* Scrollable Day Columns */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            css={{
              flex: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              '::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 146px)',
                minWidth: 'fit-content',
              }}
            >
              {ORDERED_DAYS.map((dayIndex) => {
                const events = eventsByDay[dayIndex];

                return (
                  <div
                    key={dayIndex}
                    css={{
                      position: 'relative',
                      height: totalHeight,
                      borderLeft: '1px solid var(--fabric-color-border-subtle)',
                      borderRight:
                        dayIndex === ORDERED_DAYS[ORDERED_DAYS.length - 1]
                          ? '1px solid var(--fabric-color-border-subtle)'
                          : 'none',
                    }}
                  >
                    {/* Hour lines */}
                    {timeSlots.map((time, i) => (
                      <div
                        key={time}
                        style={{
                          position: 'absolute',
                          top: i * PIXELS_PER_HOUR,
                          left: 0,
                          right: 0,
                          borderTop: '1px solid var(--fabric-color-border-subtle)',
                        }}
                      />
                    ))}

                    {/* Events */}
                    {events.map((event) => {
                      const top = getPosition(event.start, START_HOUR, PIXELS_PER_HOUR);
                      const height = getPosition(event.end, START_HOUR, PIXELS_PER_HOUR) - top;

                      return (
                        <div
                          key={`${event.type}-${event.start}-${event.end}`}
                          css={{
                            position: 'absolute',
                            top,
                            left: 0,
                            right: 0,
                            height,
                            padding: '0 var(--fabric-space-padding-xs)',
                            fontSize: 12,
                            background:
                              event.type === 'break' ? 'var(--fabric-color-indigo-5)' : 'var(--fabric-color-green-5)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 2,
                          }}
                        >
                          {event.type === 'break' && (
                            <>
                              <Truncated
                                css={{ fontWeight: 'bold', color: 'var(--fabric-color-indigo-70)', minWidth: 0 }}
                              >
                                {event.label}
                              </Truncated>
                              {height >= 48 && (
                                <Text size='small'>
                                  {formatTime(event.start)} - {formatTime(event.end)}
                                </Text>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
